import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useTypedSelector, callLLMAction, prefetchCallLLMActionSource, type AppDispatch } from "@circuitry-ai/doc-data";
import { VoiceCaptureIconMenu, VoiceFormAssistant } from "@circuitry-ai/doc-ui";

type FTIRPrefill = Partial<{
  jobCardNo: string;
  dealerCode: string;
  dealerName: string;
  registrationNo: string;
  modelName: string;
  vin: string;
  chasisNo: string;
  mileage: string;
  engineNo: string;
  saleDate: string;
  dateOfIncident: string;
  actualProblem: string;
  customerReported: string;
  defectLocation: string;
  defectConditions: string;
  checkedBeforeConclusion: string;
  dealerConclusion: string;
  diagnosticCode: string;
  possibleCausalPartNo: string;
  howRepaired: string;
}>;

type VoiceContext = {
  conditions?: string[];
  speedRelated?: "yes" | "no";
  rpmRelated?: "yes" | "no";
  speedFrom?: string;
  speedTo?: string;
  rpmFrom?: string;
  rpmTo?: string;
  speedComment?: string;
  rpmComment?: string;
  roadConditions?: string[];
};

type AIAnalysis = {
  prefill?: FTIRPrefill;
  voiceContext?: VoiceContext;
  questions?: string[];
};

const MAX_COMPLAINT_CHARS = 5000;

const jobCard = {
  jobCardNo: "JC25016553",
  dealerCode: "DLR-1024",
  dealerName: "JBR GLOBAL MOTORS",
  customerId: "2246332257",
  customerName: "M/S JBR GLOBAL",
  regNo: "BS3F3P3147",
  regDate: "10/09/2025",
  chassisNo: "6024144",
  engineNo: "9206893",
  keyNo: "3372",
  mileage: "121876",
  vin: "MA3EWB615G1234567",
  saleDate: "16/03/2023",
  serviceModel: "MARUTI 800",
  contact: "9879797036",
};

const callPortkey = (dispatch: AppDispatch, systemPrompt: string, userPrompt: string): Promise<string> =>
  callLLMAction({ dispatch, systemPrompt, userPrompt });

const extractJson = (text: string) => {
  const fenceStart = text.toLowerCase().indexOf("```json");
  if (fenceStart !== -1) {
    const afterStart = text.indexOf("\n", fenceStart);
    const contentStart = afterStart !== -1 ? afterStart + 1 : fenceStart + 7;
    const fenceEnd = text.indexOf("```", contentStart);
    if (fenceEnd !== -1) {
      return text.slice(contentStart, fenceEnd).trim();
    }
  }
  return text.trim();
};

const summarizeComplaintWithAI = async (dispatch: AppDispatch, conversation: string) => {
  if (!conversation.trim()) return "";
  const systemPrompt = `You are a service advisor assistant. Summarize the customer's complaint based on the full conversation.
Return plain text only (no JSON, no bullets). Keep it concise, factual, and easy to read.
Do not invent details. Keep critical specifics: symptoms, conditions, timing, speed/RPM, noises, smells, location.
Limit to ${MAX_COMPLAINT_CHARS} characters.`;
  const userPrompt = `Full conversation:
"""${conversation}"""

Return the complaint summary only.`;
  const response = await callPortkey(dispatch, systemPrompt, userPrompt);
  const summary = response.trim();
  return summary.length > MAX_COMPLAINT_CHARS ? summary.slice(0, MAX_COMPLAINT_CHARS).trim() : summary;
};

const analyzeComplaintWithAI = async (
  dispatch: AppDispatch,
  complaint: string,
  correctiveAction: string,
  languageCode: string,
  languageName?: string
): Promise<AIAnalysis> => {
  const languageDirective =
    languageCode === "en-US"
      ? "English"
      : languageName
      ? `${languageName} (${languageCode})`
      : languageCode;
  const systemPrompt = `You are an Automotive(cars) expert. Extract structured FTIR form context from a customer complaint.
Return ONLY JSON with shape:
{
  "prefill": {
    "jobCardNo": "",
    "dealerCode": "",
    "dealerName": "",
    "registrationNo": "",
    "modelName": "",
    "vin": "",
    "chasisNo": "",
    "mileage": "",
    "engineNo": "",
    "saleDate": "",
    "dateOfIncident": "",
    "actualProblem": "",
    "customerReported": "",
    "defectLocation": "",
    "defectConditions": "",
    "checkedBeforeConclusion": "",
    "dealerConclusion": "",
    "diagnosticCode": "",
    "possibleCausalPartNo": "",
    "howRepaired": ""
  },
  "voiceContext": {
    "conditions": [],
    "speedRelated": "yes|no",
    "rpmRelated": "yes|no",
    "speedFrom": "",
    "speedTo": "",
    "rpmFrom": "",
    "rpmTo": "",
    "speedComment": "",
    "rpmComment": "",
    "roadConditions": []
  },
  "questions": []
}
Rules:
- Use empty strings when unknown.
- "questions" should be a list of ultra-short customer prompts (2–6 words each). They should read like quick hints, not full sentences. Only ask for details the customer can provide (symptoms, when/where it occurs, conditions, noises, smells).
- Return "questions" in ${languageDirective}.
- Rank questions by highest impact on missing fields in this order: defectConditions, defectLocation, actualProblem, customerReported, roadConditions, speedRelated/speed values, rpmRelated/rpm values.
- Make every question directly target ONE missing field or ambiguity in the complaint. Avoid generic or redundant prompts.
- Generate questions ONLY from the complaint text. Do not create questions based on the corrective action.
- Do NOT ask dealer/technical/internal questions (no diagnostics, parts, codes, or conclusions). Those should be derived later.
- Determine speedRelated/rpmRelated based on the complaint. If speedRelated is "yes", also populate speedFrom/speedTo when present and add a clear speedComment. If rpmRelated is "yes", also populate rpmFrom/rpmTo when present and add a clear rpmComment.
- If any of these are undetermined, include customer questions to clarify them: defectConditions (when it happened), speedRelated/rpmRelated plus their values, and roadConditions.
- If the complaint is related to a safety issue (brakes, steering, lights, airbags, etc.), include questions to clarify the situation, and ask whether the vehicle is parked safely.
- Field meaning hints:
  - defectLocation: where the issue is on the vehicle (system/area).
  - defectConditions: whether the defect occurs when parked or running (use "Parked", "Running", or "Parked / Running").
  - actualProblem: the concrete technical problem or symptom.
  - customerReported: the customer's own words of the issue.
  - checkedBeforeConclusion: what was inspected before concluding.
  - dealerConclusion: technician/dealer conclusion after analysis.
  - diagnosticCode: OBD/diagnostic code if mentioned.
- "conditions" must be from this list only:
At Engine Starting, After Engine Start, Starting Off, Accelerating, Cruising, Decelerating, Braking,
Before Stop, Idling, Right Turning, Left Turning, Up Hill, Down Hill, Reverse, Heavy Traffic,
Shifting Gear, Over Bump.
- "roadConditions" must be from this list only:
City Street, Local Road, Highway / Freeway, Mountain, Slope, Sand, High Land, Race Track,
Bump, Road Hole, Hump, Rut / Groove, Asphalt, Concrete, Soling, Flat dirt, Gravel, Sand, Mud,
Fresh Snow, Compacted Snow, Slush, Ice, Water Depth.
- Do not include extra keys.
- Treat corrective action as supplemental context, not as the customer complaint. Use it ONLY for repair/diagnosis-related fields (diagnosticCode, possibleCausalPartNo, howRepaired, checkedBeforeConclusion, dealerConclusion) and do NOT use it to fill or infer any other fields.
- If a diagnostic code is explicitly mentioned in the complaint or corrective action, use it.
- If not mentioned, infer the most likely valid OBD-II code for a Maruti Suzuki vehicle based on the complaint and corrective action.
- The diagnostic code must match the pattern [PBCU][0-9]{4}. If you cannot infer confidently, return an empty string.`;

  const userPrompt = `Complaint (customer reported):
"""${complaint}"""

Corrective action (supplemental, not customer complaint):
"""${correctiveAction}"""

Job card context (use this to fill matching FTIR fields when applicable):
jobCardNo: ${jobCard.jobCardNo}
dealerCode: ${jobCard.dealerCode}
dealerName: ${jobCard.dealerName}
registrationNo: ${jobCard.regNo}
modelName: ${jobCard.serviceModel}
vin: ${jobCard.vin}
chasisNo: ${jobCard.chassisNo}
mileage: ${jobCard.mileage}
engineNo: ${jobCard.engineNo}
saleDate: ${jobCard.saleDate}
dateOfIncident: ${jobCard.regDate}

Return JSON only.`;

  const response = await callPortkey(dispatch, systemPrompt, userPrompt);
  const jsonText = extractJson(response);
  const parsed = JSON.parse(jsonText);
  return parsed ?? {};
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start gap-2">
    <div className="w-[110px] font-semibold text-slate-900">{label}</div>
    <div className="flex-1 text-slate-800">{value}</div>
  </div>
);

const SectionHeader = ({ title }: { title: string }) => (
  <div className="border-b border-slate-800 bg-slate-100 px-2 py-[2px] text-[11px] font-semibold uppercase">
    {title}
  </div>
);

export default function JobCardScreenReplica() {
  const dispatch = useAppDispatch();
  const authToken = useTypedSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const complaintContainerRef = useRef<HTMLDivElement>(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState({
    code: "en-US",
    name: "English",
  });
  const [prefill, setPrefill] = useState<FTIRPrefill>({});
  const [voiceContext, setVoiceContext] = useState<VoiceContext>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const lastAnalyzedRef = useRef<string>("");
  const lastPartialRef = useRef<string>("");
  const conversationRef = useRef<string[]>([]);
  const summaryRunIdRef = useRef(0);
  const manualToggleRef = useRef<"on" | "off" | null>(null);
  const restartTimerRef = useRef<number | null>(null);


  const ftirTransferPrefill = useMemo<FTIRPrefill>(
    () => ({
      dealerCode: prefill.dealerCode || jobCard.dealerCode,
      dealerName: prefill.dealerName || jobCard.dealerName,
      jobCardNo: prefill.jobCardNo || jobCard.jobCardNo,
      registrationNo: prefill.registrationNo || jobCard.regNo,
      modelName: prefill.modelName || jobCard.model,
      vin: prefill.vin || jobCard.vin,
      chasisNo: prefill.chasisNo || jobCard.chassisNo,
      mileage: prefill.mileage || jobCard.mileage,
      engineNo: prefill.engineNo || jobCard.engineNo,
      saleDate: prefill.saleDate || jobCard.saleDate,
    }),
    [prefill]
  );

  const prefillSummary = useMemo(() => {
    const entries = Object.entries(prefill).filter(([, value]) => value);
    return entries.map(([key, value]) => ({
      key,
      value: String(value),
    }));
  }, [prefill]);

  const requiredPrefillKeys: Array<keyof FTIRPrefill> = useMemo(
    () => [
      "dealerCode",
      "dealerName",
      "jobCardNo",
      "registrationNo",
      "modelName",
      "vin",
      "chasisNo",
      "mileage",
      "engineNo",
      "saleDate",
      "dateOfIncident",
      "actualProblem",
      "customerReported",
      "defectLocation",
      "defectConditions",
    ],
    []
  );

  const hasMissingRequired = useMemo(() => {
    return requiredPrefillKeys.some((key) => !prefill?.[key]);
  }, [prefill, requiredPrefillKeys]);

  const hasMissingContext = useMemo(() => {
    const missingRoad = !voiceContext?.roadConditions || voiceContext.roadConditions.length === 0;
    const missingSpeed =
      !voiceContext?.speedRelated ||
      (voiceContext.speedRelated === "yes" &&
        !voiceContext.speedFrom &&
        !voiceContext.speedTo &&
        !voiceContext.speedComment);
    const missingRpm =
      !voiceContext?.rpmRelated ||
      (voiceContext.rpmRelated === "yes" &&
        !voiceContext.rpmFrom &&
        !voiceContext.rpmTo &&
        !voiceContext.rpmComment);
    return missingRoad || missingSpeed || missingRpm;
  }, [voiceContext]);

  const qualityMetric = useMemo(() => {
    const filledPrefill = requiredPrefillKeys.filter((key) => !!prefill?.[key]).length;
    const speedFilled = !!voiceContext?.speedRelated;
    const rpmFilled = !!voiceContext?.rpmRelated;
    const roadFilled = !!voiceContext?.roadConditions?.length;
    const totalFields = requiredPrefillKeys.length + 3;
    const filledFields =
      filledPrefill + (speedFilled ? 1 : 0) + (rpmFilled ? 1 : 0) + (roadFilled ? 1 : 0);
    const percent = totalFields ? Math.round((filledFields / totalFields) * 100) : 0;
    const missing: string[] = [];
    requiredPrefillKeys.forEach((key) => {
      if (!prefill?.[key]) missing.push(key);
    });
    if (!speedFilled) missing.push("speed info");
    if (!rpmFilled) missing.push("RPM info");
    if (!roadFilled) missing.push("road conditions");
    const colorClass =
      percent >= 80
        ? "text-emerald-600"
        : percent >= 50
          ? "text-amber-600"
          : "text-red-600";
    const trackClass =
      percent >= 80
        ? "text-emerald-200"
        : percent >= 50
        ? "text-amber-200"
        : "text-red-200";
    return { percent, colorClass, trackClass, filledFields, totalFields, missing };
  }, [prefill, requiredPrefillKeys, voiceContext]);

  useEffect(() => {
    // Warm the action's source_id cache up front so voice capture doesn't pay for the
    // `GET /api/v1/sources?source_type=action` lookup on its first LLM call. Skip until we
    // have an auth token so this doesn't fire (and 401) before login/session restore completes.
    if (!authToken) return;
    prefetchCallLLMActionSource(dispatch);
  }, [dispatch, authToken]);

  useEffect(() => {
    return () => {
      if (restartTimerRef.current) {
        window.clearTimeout(restartTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasMissingRequired && !hasMissingContext && followUpQuestions.length) {
      setFollowUpQuestions([]);
    }
  }, [hasMissingRequired, hasMissingContext, followUpQuestions.length]);

  useEffect(() => {
    if (complaint.trim() || correctiveAction.trim()) {
      lastAnalyzedRef.current = "";
    }
  }, [selectedLanguage.code]);

  useEffect(() => {
    const cleanedComplaint = complaint.trim();
    const cleanedAction = correctiveAction.trim();
    const analysisKey = `${cleanedComplaint}|${cleanedAction}|${selectedLanguage.code}`;
    if (!cleanedComplaint && !cleanedAction) {
      setFollowUpQuestions([]);
      return;
    }
    if (analysisKey === lastAnalyzedRef.current) return;
    const handle = window.setTimeout(async () => {
      const latestComplaint = complaint.trim();
      const latestAction = correctiveAction.trim();
      const latestKey = `${latestComplaint}|${latestAction}|${selectedLanguage.code}`;
      if ((!latestComplaint && !latestAction) || latestKey === lastAnalyzedRef.current) return;
      setAnalysisError(null);
      setIsAnalyzing(true);
      try {
        const ai = await analyzeComplaintWithAI(
          dispatch,
          latestComplaint,
          latestAction,
          selectedLanguage.code,
          selectedLanguage.name
        );
        setPrefill(ai?.prefill ?? {});
        setVoiceContext(ai?.voiceContext ?? {});
        setFollowUpQuestions(
          Array.isArray(ai?.questions) ? ai.questions.filter(Boolean) : []
        );
        lastAnalyzedRef.current = latestKey;
      } catch (error) {
        setAnalysisError(error instanceof Error ? error.message : "AI analysis failed.");
        setPrefill({});
        setVoiceContext({});
        setFollowUpQuestions([]);
        lastAnalyzedRef.current = latestKey;
      } finally {
        setIsAnalyzing(false);
      }
    }, 800);
    return () => window.clearTimeout(handle);
  }, [complaint, correctiveAction, selectedLanguage.code, selectedLanguage.name]);

  const handleProceed = () => {
    const normalizeText = (value: string) => value.trim().toLowerCase();
    const combinedText = `${correctiveAction} ${complaint}`.trim();
    const combinedLower = normalizeText(combinedText);
    const diagMatch = combinedText.match(/\b([A-Z]\d{4})\b/);
    const diagnosticCode = diagMatch?.[1] ?? "";
    const partMatch = combinedText.match(/\b([A-Z0-9]{5,})\b/);
    const possibleCausalPartNo = partMatch?.[1] ?? "";
    const deriveRepaired = () => {
      if (!combinedLower) return null;
      if (
        /\b(not repaired|unrepaired|no repair|not fixed|not done|pending repair|repair pending)\b/.test(
          combinedLower
        )
      ) {
        return "no";
      }
      if (
        /\b(repaired|fixed|rectified|replaced|resolved|done|completed|repair done|repair completed)\b/.test(
          combinedLower
        )
      ) {
        return "yes";
      }
      return null;
    };
    const deriveSolved = () => {
      if (!combinedLower) return null;
      if (
        /\b(not solved|unsolved|not resolved|still|persists|recurs|recurring|issue remains)\b/.test(
          combinedLower
        )
      ) {
        return "no";
      }
      if (/\b(solved|resolved|fixed|ok now|working|no issue)\b/.test(combinedLower)) {
        return "yes";
      }
      return null;
    };
    const repairedValue = deriveRepaired();
    const solvedValue = deriveSolved();
    const nextPrefill: FTIRPrefill = {
      ...prefill,
      ...ftirTransferPrefill,
      ...(correctiveAction.trim() ? { howRepaired: correctiveAction.trim() } : {}),
      ...(diagnosticCode ? { diagnosticCode } : {}),
      ...(possibleCausalPartNo ? { possibleCausalPartNo } : {}),
      ...(repairedValue ? { checkedBeforeConclusion: repairedValue } : {}),
      ...(solvedValue ? { dealerConclusion: solvedValue } : {}),
    };
    navigate("/ftir", {
      state: {
        prefill: nextPrefill,
        complaint,
        voiceContext,
        fromJobCard: true,
        diagnosticCode,
        possibleCausalPartNo,
        repaired: repairedValue,
        solvedAfterRepair: solvedValue,
      },
    });
  };

  const handleStartNewJob = () => {
    manualToggleRef.current = "off";
    setVoiceEnabled(false);
    setComplaint("");
    setCorrectiveAction("");
    setLiveTranscript("");
    setSelectedLanguage({
      code: "en-US",
      name: "English",
    });
    setPrefill({});
    setVoiceContext({});
    setAnalysisError(null);
    setIsAnalyzing(false);
    setFollowUpQuestions([]);
    lastAnalyzedRef.current = "";
    lastPartialRef.current = "";
    conversationRef.current = [];
    summaryRunIdRef.current += 1;
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  };

  const handleToggleVoice = () => {
    const nextEnabled = !voiceEnabled;
    manualToggleRef.current = nextEnabled ? "on" : "off";
    setVoiceEnabled(nextEnabled);
    if (!nextEnabled) {
      if (restartTimerRef.current) {
        window.clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
      setLiveTranscript("");
    }
  };

  const handleAssistantToggle = (nextEnabled: boolean) => {
    if (nextEnabled) {
      manualToggleRef.current = "on";
      setVoiceEnabled(true);
      return;
    }
    if (manualToggleRef.current === "off") {
      setVoiceEnabled(false);
      return;
    }
    setVoiceEnabled(false);
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
    }
    restartTimerRef.current = window.setTimeout(() => {
      if (manualToggleRef.current !== "off") {
        setVoiceEnabled(true);
      }
    }, 150);
  };

  const refreshComplaintSummary = useCallback(async (segments: string[]) => {
    const runId = ++summaryRunIdRef.current;
    try {
      const summary = await summarizeComplaintWithAI(dispatch, segments.join("\n"));
      if (runId !== summaryRunIdRef.current) return "";
      setComplaint(summary);
      return summary;
    } catch {
      const fallback = segments.join(" ").slice(0, MAX_COMPLAINT_CHARS).trim();
      if (runId !== summaryRunIdRef.current) return "";
      setComplaint(fallback);
      return fallback;
    }
  }, [dispatch]);

  const parseComplaintTranscript = useCallback(
    async ({ transcript }: { transcript: string }) => {
      const cleaned = transcript.trim();
      if (!cleaned) return [];
      const nextConversation = [...conversationRef.current, cleaned];
      conversationRef.current = nextConversation;
      const summary = await refreshComplaintSummary(nextConversation);
      return cleaned
        ? [
            {
              label: "Customer Complaint",
              value: summary || cleaned,
            },
          ]
        : [];
    },
    [complaint, refreshComplaintSummary]
  );

  return (
    <div className="h-full overflow-y-auto bg-slate-200 p-3 md:p-4">
      <div className="mx-auto max-w-[1200px] border border-slate-800 bg-white text-[11px] text-slate-900">
        <div className="relative border-b border-slate-800 bg-slate-100 py-1 text-center text-[13px] font-bold">
          JOB CARD
          <button
            type="button"
            onClick={handleStartNewJob}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm border border-slate-700 bg-white px-2 py-[2px] text-[10px] font-semibold text-slate-800 hover:bg-slate-100"
          >
            New Job
          </button>
        </div>

        <div className="grid grid-cols-1 gap-0 border-b border-slate-800 md:grid-cols-12">
          <div className="col-span-12 border-b border-slate-800 p-2 md:col-span-7 md:border-b-0 md:border-r">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <InfoRow label="JC No" value={jobCard.jobCardNo} />
                <InfoRow label="Customer ID" value={jobCard.customerId} />
                <InfoRow label="Name" value={jobCard.customerName} />
                <InfoRow label="Address" value="GROWL, B No.-25/16, VPO-JATAULI, DISTT. GURUGRAM" />
              </div>
              <div className="space-y-1">
                <InfoRow label="Dealer Code" value={jobCard.dealerCode} />
                <InfoRow label="Dealer Name" value={jobCard.dealerName} />
                <InfoRow label="Reg No." value={jobCard.regNo} />
                <InfoRow label="VIN" value={jobCard.vin} />
                <InfoRow label="Reg Date" value={jobCard.regDate} />
                <InfoRow label="Chassis" value={jobCard.chassisNo} />
                <InfoRow label="Engine No." value={jobCard.engineNo} />
                <InfoRow label="Key No." value={jobCard.keyNo} />
              </div>
            </div>
          </div>
          <div className="col-span-12 p-2 md:col-span-5">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <InfoRow label="Mileage" value={jobCard.mileage} />
                <InfoRow label="Model" value={jobCard.serviceModel} />
                <InfoRow label="Sale Date" value={jobCard.saleDate} />
                <InfoRow label="Contact" value={jobCard.contact} />
              </div>
              <div className="flex flex-col items-center justify-center gap-2 border border-slate-800 bg-slate-50 p-2">
                <div className="text-[10px] font-semibold">BARCODE</div>
                <div className="h-8 w-full max-w-[160px] border border-slate-700 bg-gradient-to-r from-slate-900 via-slate-400 to-slate-900 md:w-[160px]" />
                <div className="text-[10px] tracking-[0.2em]">*#{jobCard.jobCardNo}*</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 border-b border-slate-800 md:grid-cols-12">
          <div className="col-span-12 border-b border-slate-800 md:col-span-3 md:border-b-0 md:border-r">
            <SectionHeader title="Service Maintenance Service" />
            <div className="space-y-1 p-2">
              <InfoRow label="Schedule Service" value="RMS120" />
              <InfoRow label="Service Model" value={jobCard.serviceModel} />
              <InfoRow label="Job Type" value="Periodic Service" />
            </div>
          </div>
          <div className="col-span-12 border-b border-slate-800 md:col-span-6 md:border-b-0 md:border-r">
            <SectionHeader title="Observations & Instructions" />
            <div ref={complaintContainerRef} className="relative space-y-2 p-2">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="customer-complaint" className="block text-[11px] font-semibold">
                    Customer Complaint
                  </label>
                  <div className="relative">
                    <textarea
                      id="customer-complaint"
                      data-voice-complaint
                      className="min-h-[96px] w-full rounded-sm border border-slate-700 bg-yellow-100 p-2 pr-24 pb-10 text-[11px]"
                      placeholder="Describe the issue reported by the customer..."
                      value={voiceEnabled && liveTranscript ? liveTranscript : complaint}
                      onChange={(event) => {
                        setComplaint(event.target.value);
                        setLiveTranscript("");
                      }}
                    />
                    <div className="absolute bottom-2 right-2 z-20">
                      <VoiceCaptureIconMenu
                        active={voiceEnabled}
                        onToggle={handleToggleVoice}
                        panelClassName="w-[260px]"
                        languagePanel={({ closePanel }) => (
                          <VoiceFormAssistant
                            enabled={voiceEnabled}
                            dispatch={dispatch}
                            containerRef={complaintContainerRef}
                            selector="textarea[data-voice-complaint]"
                            narrationMode={true}
                            continuousNarration={true}
                            narrationSkipWelcome={true}
                            showNarrationOverlay={false}
                            parseTranscript={parseComplaintTranscript}
                            onNarrationPartial={(partial) => {
                              if (!partial) {
                                lastPartialRef.current = "";
                                setLiveTranscript("");
                                return;
                              }
                              const prev = lastPartialRef.current;
                              const next =
                                prev && !partial.toLowerCase().startsWith(prev.toLowerCase())
                                  ? `${prev} ${partial}`.trim()
                                  : partial;
                              lastPartialRef.current = next;
                              setLiveTranscript(next);
                            }}
                            onNarrationComplete={(finalText) => {
                              if (finalText) {
                                setLiveTranscript("");
                                lastPartialRef.current = "";
                              }
                            }}
                            statusText={{
                              idle: "Voice capture idle",
                              listening: "Listening for complaint",
                              processing: "Analyzing complaint",
                            }}
                            showLanguageSelector={true}
                            showControls={true}
                            showToggleButton={false}
                            onToggle={handleAssistantToggle}
                            onExit={() => {
                              manualToggleRef.current = "off";
                            }}
                            onLanguageChange={(code, name) => {
                              setSelectedLanguage({ code, name: name ?? code });
                              closePanel();
                            }}
                            controlsClassName="w-[260px]"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <label htmlFor="corrective-action" className="block text-[11px] font-semibold">
                    Corrective Action
                  </label>
                  <textarea
                    id="corrective-action"
                    className="min-h-[96px] w-full rounded-sm border border-slate-700 bg-white p-2 text-[11px]"
                    placeholder="Add repair notes, diagnostics, or corrective actions..."
                    value={correctiveAction}
                    onChange={(event) => setCorrectiveAction(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isAnalyzing ? (
                  <div className="text-[10px] font-semibold text-slate-600">Analyzing complaint...</div>
                ) : null}
                {analysisError ? (
                  <div className="text-[10px] text-red-600">{analysisError}</div>
                ) : null}
                {(hasMissingRequired || hasMissingContext) && followUpQuestions.length ? (
                  <div className="w-full rounded-sm border border-slate-300 bg-slate-50 p-2 text-[11px] text-slate-700">
                    <div className="mb-1 text-[10px] font-semibold uppercase text-slate-600">
                      Questions to Ask Customer
                    </div>
                    <ul className="list-disc space-y-1 pl-4">
                      {followUpQuestions.map((question, index) => (
                        <li key={`${question}-${index}`}>{question}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-3">
            <SectionHeader title="Other Information" />
            <div className="space-y-1 p-2">
              <InfoRow label="Insurance" value="Yes" />
              <InfoRow label="MCP Enroll No." value="N/A" />
              <InfoRow label="Type of Car" value="N/A" />
              <InfoRow label="Repair Location" value="W/S" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 border-b border-slate-800 md:grid-cols-12">
          <div className="col-span-12 border-b border-slate-800 md:col-span-6 md:border-b-0 md:border-r">
            <SectionHeader title="Demanded / Suggested Jobs" />
            <div className="p-2 text-[10px]">
              PMS 50/70/100 Service<br />
              Warning Light Glowing / Blinking
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <SectionHeader title="Delivery" />
            <div className="grid grid-cols-2 gap-2 p-2">
              <InfoRow label="Date" value="10-Sep-25" />
              <InfoRow label="Time" value="06:03 PM" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 border-b border-slate-800 md:grid-cols-12">
          <div className="col-span-12 border-b border-slate-800 md:col-span-6 md:border-b-0 md:border-r">
            <SectionHeader title="Vehicle History (Last)" />
            <div className="space-y-1 p-2 text-[10px]">
              <div>09168114102 - Casset</div>
              <div>11518003010 - Plug, Oil Drain</div>
              <div>16100588720 - Filters, AC Oil</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <SectionHeader title="Vehicle History (2nd Last)" />
            <div className="space-y-1 p-2 text-[10px]">
              <div>09168114102 - Casset</div>
              <div>11518003010 - Plug, Oil Drain</div>
              <div>16100588720 - Filters, AC Oil</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 md:grid-cols-12">
          <div className="col-span-12 border-b border-slate-800 md:col-span-8 md:border-b-0 md:border-r">
            <SectionHeader title="FTIR Prefill Preview" />
            <div className="space-y-2 p-2 text-[11px]">
              {complaint.trim() ? (
                <div className="rounded-sm border border-slate-300 bg-white p-2 text-[11px] text-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="relative h-12 w-12">
                      <svg viewBox="0 0 36 36" className="h-12 w-12">
                        <path
                          d="M18 2.0845a 15.9155 15.9155 0 1 1 0 31.831a 15.9155 15.9155 0 1 1 0 -31.831"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className={qualityMetric.trackClass}
                        />
                        <path
                          d="M18 2.0845a 15.9155 15.9155 0 1 1 0 31.831a 15.9155 15.9155 0 1 1 0 -31.831"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeDasharray={`${qualityMetric.percent}, 100`}
                          className={qualityMetric.colorClass}
                        />
                      </svg>
                      <div
                        className={
                          "absolute inset-0 flex items-center justify-center text-[11px] font-semibold " +
                          qualityMetric.colorClass
                        }
                      >
                        {qualityMetric.percent}%
                      </div>
                    </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase text-slate-600">
                      Complaint Coverage
                    </div>
                    {qualityMetric.missing.length ? (
                      <div className="mt-1 text-[10px] text-slate-500">
                        Missing: {qualityMetric.missing.join(", ")}
                      </div>
                    ) : (
                      <div className="mt-1 text-[10px] text-emerald-600">
                        All key fields covered
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
              {prefillSummary.length ? (
                <div className="rounded-sm border border-slate-300 bg-slate-100">
                  <div className="grid grid-cols-1 gap-x-3 gap-y-1 px-2 py-1 text-[11px] text-slate-800 md:grid-cols-2">
                    {prefillSummary.map((item) => (
                      <div key={item.key} className="grid grid-cols-[130px_1fr] gap-2">
                        <div className="truncate font-semibold uppercase text-slate-600">{item.key}</div>
                        <div className="truncate text-slate-800" title={item.value}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-slate-500">Add a complaint to generate FTIR prefill data.</div>
              )}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <SectionHeader title="Next Step" />
            <div className="flex h-full flex-col justify-between gap-3 p-2">
              <div className="text-[11px] text-slate-700">
                Capture the complaint by voice, review the extracted insights, then continue to the FTIR
                form to fill remaining fields.
              </div>
              <button
                type="button"
                className="h-10 rounded-sm border border-blue-800 bg-blue-700 text-[12px] font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleProceed}
                disabled={!complaint.trim()}
              >
                Open FTIR Form
              </button>
              <div className="text-[10px] text-slate-500">
                Voice capture only targets the customer complaint field.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
